import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link as MuiLink,
  Skeleton,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL } from '../Config';
import config from "../common/Config";
import { Iconify } from "src/components/iconify";
import "../common/commonStyles.css";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";

const ShortQty = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [newSales, setNewSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const accessToken = localStorage.getItem('accessToken');

  const columns = [
    { id: "picklist_number", label: "Picklist", width: 120 },
    { id: "route", label: "Route Name", width: 200 },
    { id: "name", label: "Picker Name", width: 200 },
    { id: "print_on", label: "Picked Allocate Date", width: 180 },
    { id: "action", label: "Action", width: 100 },
  ];

  const fetchData = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = { authorization: `Bearer ${token}` };
      let body;
      if (useEncrypt) {
        const plain = {
          searchByFromdate: fromDate,
          searchByTodate: toDate,
          draw: "1",
          start: "0",
          length: "1000",
          role: selectedRole,
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          setNewSales([]);
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        body = fd;
      } else {
        const formData = new FormData();
        formData.append('searchByFromdate', fromDate);
        formData.append('searchByTodate', toDate);
        formData.append('draw', 1);
        formData.append('start', 0);
        formData.append('length', 1000);
        formData.append('role', selectedRole);
        body = formData;
      }

      const response = await fetch(`${API_URL}/short/picklist`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch short qty data');
      }

      const json = await response.json();
      const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
      setNewSales(Array.isArray(data.data) ? data.data : []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedRole]);

  const filteredSales = useMemo(() => {
    if (!searchTerm.trim()) {
      return newSales;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newSales.filter((item) => {
      return (
        (item.picklist_number && String(item.picklist_number).toLowerCase().includes(searchLower)) ||
        (item.route && item.route.toLowerCase().includes(searchLower)) ||
        (item.name && item.name.toLowerCase().includes(searchLower)) ||
        (item.print_on && item.print_on.toLowerCase().includes(searchLower))
      );
    });
  }, [newSales, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, page, rowsPerPage]);

  const renderCell = (columnId, row) => {
    if (columnId === "picklist_number") {
      return (
        <MuiLink
          href={`${config.BASE_URL}/picking/view_short_qty/${row.picklist_number}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "underline !important",
            textDecorationColor: "currentColor",
            textUnderlineOffset: "2px",
            "&:hover": {
              textDecoration: "underline !important",
              textDecorationColor: "currentColor"
            }
          }}
        >
          {row.picklist_number}
        </MuiLink>
      );
    }
    if (columnId === "action") {
      return (
        <IconButton
          size="small"
          href={`${config.BASE_URL}/picking/view_short_qty/${row.picklist_number}`}
          target="_blank"
   
          title="View"
        >
          <Iconify icon="eva:eye-fill" width={18} height={18} />
        </IconButton>
      );
    }
    return row[columnId] ?? "";
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography className="page-title">
                  Short Qty
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Search short qty..."
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <TableContainer sx={{ width: "100%", overflowX: "auto"}}>
            <Table
              size="medium"
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.id === "action" ? "center" : "left"}
                      sx={{
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : "left"}
                          sx={{ py: 1, fontSize: "0.75rem", width: column.width ? `${column.width}px` : "auto" }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedSales.length > 0 ? (
                  paginatedSales.map((row, rowIndex) => (
                    <TableRow
                      key={row.picklist_number || rowIndex}
                      hover
                      className="table-cell-style"
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : "left"}
                          sx={{
                      
                            width: column.width ? `${column.width}px` : "auto"
                          }}
                        >
                          {renderCell(column.id, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                      <Typography align="center">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={filteredSales.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ShortQty;
